import { Info } from "lucide-react";
import { useState } from "react";

/**
 * NCBadge — surfaces the "Numerus Clausus" reality on any German degree card.
 *
 * German Psychology / Medicine / Law route via Uni-Assist with a 20 % non-EU
 * quota and Abitur-equivalent grade caps. International applicants routinely
 * discover this only after applying — this badge surfaces the friction on
 * every card so it's a decision, not a surprise.
 *
 * Renders nothing when the degree has no `numerus_clausus` code in its warnings.
 */
export default function NCBadge({ warnings, testid }) {
  const [open, setOpen] = useState(false);
  const hasNc = Array.isArray(warnings)
    && warnings.some((w) => w?.code === "numerus_clausus");
  if (!hasNc) return null;
  const ncText = warnings.find((w) => w.code === "numerus_clausus")?.text || "";

  return (
    <span
      data-testid={testid || "nc-badge"}
      className="relative inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-red-800"
    >
      <span aria-hidden>🇩🇪</span>
      <span>NC subject</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-label="What does Numerus Clausus mean?"
        data-testid="nc-badge-info-btn"
        className="ml-0.5 text-red-600 hover:text-red-800"
      >
        <Info className="h-3 w-3" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-10 top-full mt-1 left-0 w-72 rounded-md border border-stone-200 bg-white shadow-lg p-3 text-[11px] normal-case tracking-normal text-stone-700 font-sans"
          data-testid="nc-badge-tooltip"
        >
          <div className="font-editorial text-sm text-stone-900 mb-1">
            Numerus Clausus (NC)
          </div>
          <p className="mb-2">
            Germany caps intake for this subject and rank-orders applicants by
            Abitur / A-Level average.
            {" "}
            <span className="font-semibold">Non-EU applicants route via Uni-Assist</span> and
            compete inside a ~20 % international quota — offer rates are often
            below 15 %.
          </p>
          {ncText && (
            <p className="text-stone-500 border-t border-stone-100 pt-2">
              This course: {ncText}
            </p>
          )}
        </div>
      )}
    </span>
  );
}
